"use client";

import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ChevronsUpDown } from "lucide-react";
import { useEffect, useRef } from "react";
import { NumericFormat } from "react-number-format";
import { ALL_CHAINS, RELAY_SOLANA_MAINNET_CHAIN_ID, getAvailableChainIds } from "../../../../anyspend/index.js";
import { cn } from "../../../../shared/utils/index.js";
import { TokenSelector } from "@relayprotocol/relay-kit-ui";
import { ChainTokenIcon } from "./ChainTokenIcon.js";
export function OrderTokenAmountFiat({
  disabled,
  inputValue,
  onChangeInput,
  context,
  address,
  chainId,
  setChainId,
  token,
  setToken,
  className
}) {
  // Track previous token to detect changes
  const prevTokenRef = useRef(token.address);
  useEffect(() => {
    // Only trigger when token actually changes
    if (prevTokenRef.current !== token.address) {
      console.log(`Token changed from ${prevTokenRef.current} to ${token.address}`);
      // For "from" context, reset to default value when token changes
      if (context === "from") {
        // Reset input to default for new token
        onChangeInput("0.01");
      }
      // Update ref to current token
      prevTokenRef.current = token.address;
    }
  }, [token.address, chainId, context, onChangeInput]);
  const handleTokenSelect = newToken => {
    // Mark that we're about to change tokens
    prevTokenRef.current = "changing"; // Temporary value to force effect
    // Set the chain ID first
    setChainId(newToken.chainId);
    // Then set the new token
    setToken({
      address: newToken.address,
      chainId: newToken.chainId,
      // Use the new chain ID
      decimals: newToken.decimals,
      metadata: {
        logoURI: newToken.logoURI
      },
      name: newToken.name,
      symbol: newToken.symbol
    });
    // If this is the source token, reset the amount immediately
    if (context === "from") {
      onChangeInput("0.01");
    }
  };
  // Original token amount input design for other contexts
  return _jsx(TokenSelector, {
    address: address,
    chainIdsFilter: getAvailableChainIds(context),
    context: context,
    fromChainWalletVMSupported: true,
    isValidAddress: true,
    lockedChainIds: getAvailableChainIds(context),
    multiWalletSupportEnabled: true,
    onAnalyticEvent: undefined,
    popularChainIds: [1, 8453, RELAY_SOLANA_MAINNET_CHAIN_ID],
    setToken: handleTokenSelect,
    supportedWalletVMs: ["evm", "svm"],
    token: undefined,
    trigger: _jsxs("div", {
      className: cn("border-as-border-secondary bg-as-surface-primary flex cursor-pointer items-center justify-between rounded-xl border px-3 py-2", className),
      children: [_jsxs("div", {
        className: "flex items-center gap-3",
        children: [token.metadata?.logoURI ? _jsx(ChainTokenIcon, {
          chainUrl: ALL_CHAINS[chainId]?.logoUrl,
          tokenUrl: token.metadata.logoURI,
          className: "h-10 w-10"
        }) : _jsx("div", {
          className: "flex h-10 w-10 items-center justify-center rounded-full bg-blue-600",
          children: _jsx("span", {
            className: "font-bold text-white",
            children: token.symbol?.substring(0, 2) || "??"
          })
        }), _jsxs("div", {
          children: [_jsx("div", {
            className: "text-as-primary font-semibold",
            children: token.symbol
          }), _jsx("div", {
            className: "text-as-primary/50 text-sm",
            children: ALL_CHAINS[chainId]?.name || "Unknown"
          })]
        })]
      }), _jsxs("div", {
        className: "flex items-center gap-2",
        children: [_jsx("span", {
          className: "text-sm text-gray-600",
          children: "\u2248"
        }), _jsx(NumericFormat, {
          decimalSeparator: ".",
          allowedDecimalSeparators: [","],
          thousandSeparator: true,
          inputMode: "decimal",
          autoComplete: "off",
          autoCorrect: "off",
          type: "text",
          placeholder: "0.00",
          minLength: 1,
          maxLength: 20,
          spellCheck: "false",
          className: "text-as-primary bg-as-surface-primary w-[100px]",
          pattern: "^[0-9]*[.,]?[0-9]*$",
          disabled: disabled,
          value: inputValue,
          allowNegative: false,
          "aria-disabled": true,
          readOnly: true
        }, `input-${token.address}-${chainId}`), _jsx(ChevronsUpDown, {
          className: "h-4 w-4 cursor-pointer text-gray-400"
        })]
      })]
    })
  }, `selector-${context}-${token.address}-${chainId}`);
}